import {
	BeforeInsert,
	BeforeUpdate,
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
	OneToOne,
} from 'typeorm';
import { normalizePhoneToE164 } from '../../utils/validation';
import { BaseEntity } from './base.entity';
import { CallUserEntity } from './call-user.entity';
import { CallEntity } from './call.entity';
import { ClientAssignmentHistoryEntity } from './client-assignment-history.entity';
import { ClientWorkdUserEntity } from './client-workd-user.entity';
import { ClientEntity } from './client.entity';
import { EventUserEntity } from './event-user.entity';
import { GoalEntity } from './goal.entity';
import { IssueUserEntity } from './issue-user.entity';
import type { IssueEntity } from './issue.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { OrderEntity } from './order.entity';
import { RbacActionUserEntity } from './rbac-action-user.entity';
import { RbacRoleEntity } from './rbac-role.entity';
import { TeamUserEntity } from './team-user.entity';
import { UserClientRestrictionsEntity } from './user-client-restrictions.entity';
import { UserCompanyEntity } from './user-company.entity';
import { UserGroupEntity } from './user-group.entity';
import { UserWarehouseEntity } from './user-warehouse.entity';
import { VaultSecretsEntity } from './vault-secrets.entity';
import { WarehouseEntity } from './warehouse.entity';

@Index('unique_workd_user_id', ['workdUserId'], {})
@Index('user_titanId_uindex', ['workdUserId'], { unique: true })
@Index('idx_user_manager_user', ['fkUserIdManager'], {})
@Index('idx_user_manager_role', ['fkRbacRoleIdManager'], {})
@Entity('user')
export class UserEntity extends BaseEntity {
	@Column('varchar', { name: 'firstName', nullable: true, length: 255 })
	public firstName!: string | null;

	@Column('varchar', { name: 'lastName', nullable: true, length: 255 })
	public lastName!: string | null;

	@Column('varchar', { name: 'email', nullable: true, length: 255 })
	public email!: string | null;

	@Column('varchar', { name: 'phoneExtensionId', nullable: true, length: 255 })
	public phoneExtensionId!: string | null;

	@Column('varchar', {
		name: 'phoneExtensionNumber',
		nullable: true,
		length: 255,
	})
	public phoneExtensionNumber!: string | null;

	@Column('int', { name: 'currentLeads', default: () => "'0'" })
	public currentLeads!: number;

	@Column('int', { name: 'maxLeads', nullable: true, default: () => "'0'" })
	public maxLeads!: number | null;

	@Column('tinyint', {
		name: 'isDistributionEligible',
		width: 1,
		default: () => "'0'",
	})
	public isDistributionEligible!: boolean;

	@Column('text', { name: 'leadEligibilityFilter', nullable: true })
	public leadEligibilityFilter!: string | null;

	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('int', {
		name: '_workd__user__id',
		nullable: true,
		unsigned: true,
		unique: true,
	})
	public workdUserId!: number | null;

	@Column('varchar', { name: '_phoneNumber', nullable: true, length: 16 })
	public phoneNumber!: string | null;

	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	/**
	 * Chat availability INTENT — what the user explicitly chose, if
	 * anything. Composed at read time with Redis presence to produce
	 * effective status. Three explicit states plus null:
	 *   - `null` (default, "never chose") — follow presence. Behaves
	 *     identically to `available` for composition; kept distinct
	 *     for audit clarity ("did the user actively choose to be
	 *     online, or has the system just been deriving it?").
	 *   - `available` — "I want to be reachable when I'm here."
	 *     Effective state hinges on presence (present → available,
	 *     absent → away).
	 *   - `away` — "Don't bug me right now." Effective state is
	 *     always `away` regardless of presence — explicit intent
	 *     wins over heartbeat. DND folds in here with an optional
	 *     `availabilityMessage` for context.
	 *   - `offline` — explicit sign-off. The ONLY way effective
	 *     status reads `offline` is if the user manually set it.
	 *     "No presence" never composes to `offline` because we
	 *     can't tell from a missing heartbeat whether they signed
	 *     off or just walked away — `away` is the honest read.
	 *
	 * Composition rule (locked):
	 *   intent='offline'                      → 'offline'
	 *   intent='away'                         → 'away'
	 *   intent in {null,'available'} + present → 'available'
	 *   intent in {null,'available'} + absent  → 'away'
	 *
	 * Default `null` matches Teams/Slack — signing in IS being here,
	 * the user shouldn't have to click "Available" first. Customer
	 * routing is still gated by the separate opt-in
	 * `canReceiveCustomerChats` flag (default false), so an internal
	 * user defaulting to "available via null" never accidentally gets
	 * a customer chat.
	 *
	 * Stored as varchar (not MySQL ENUM) so adding a future value
	 * (e.g. splitting DND back out) is an application-layer change,
	 * not a column ALTER.
	 */
	@Column('varchar', {
		name: 'availabilityStatus',
		length: 16,
		nullable: true,
	})
	public availabilityStatus!: 'available' | 'away' | 'offline' | null;

	/**
	 * Optional user-supplied context for the current status — Slack/
	 * Teams style "Back at 2pm" / "Working with another customer".
	 * Internal-only; the storefront NEVER sees this (per the design
	 * memory: per-rep status doesn't leak to portal users).
	 */
	@Column('varchar', {
		name: 'availabilityMessage',
		length: 255,
		nullable: true,
	})
	public availabilityMessage!: string | null;

	/**
	 * Wall-clock of the last status change. Drives "Away since 2:14pm"
	 * tooltips on internal teammate surfaces and aging logic for the
	 * auto-away job (combined with the manual-pin window below).
	 */
	@Column('datetime', { name: 'availabilityUpdatedDate', nullable: true })
	public availabilityUpdatedDate!: Date | null;

	/**
	 * Customer-chat routing eligibility — opt-in. When `false` the user
	 * can use internal chat (DMs, groups, @mentions) but is invisible
	 * to the customer-support queue router; portal chat sessions never
	 * route to them. Default `false` so internal-only staff (warehouse,
	 * finance, dev) don't inadvertently get a customer chat the day
	 * after they sign in.
	 *
	 * Combined with `availabilityStatus` for the routing predicate:
	 *   takesCustomerChats(u) = u.canReceiveCustomerChats &&
	 *                           u.availabilityStatus === 'available'
	 *
	 * The portal `/context` endpoint computes a tenant-wide
	 * `teamAvailable` boolean from this same predicate — the storefront
	 * panel only sees the aggregate, never per-rep status.
	 */
	@Column('tinyint', {
		name: 'canReceiveCustomerChats',
		width: 1,
		default: () => "'0'",
	})
	public canReceiveCustomerChats!: boolean;

	/**
	 * Direct individual manager — a specific User who oversees this user.
	 * Independent of `fkRbacRoleIdManager`: a user can have BOTH a specific
	 * individual manager AND a managing RBAC role; "my team" resolution
	 * unions them. Used by the "manager call view" feature.
	 */
	@Column('int', {
		name: 'fk_user__id_manager',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdManager!: number | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_user__id_manager', referencedColumnName: 'id' }])
	public managerUser!: UserEntity | null;

	/**
	 * Managing RBAC role — when oversight is role-based (e.g. "sales
	 * manager" role oversees every salesperson). Common shape in current
	 * tenants. Independent of `fkUserIdManager` — both can be populated;
	 * "my team" resolution unions both rules.
	 */
	@Column('int', {
		name: 'fk__rbac_role__id_manager',
		nullable: true,
		unsigned: true,
	})
	public fkRbacRoleIdManager!: number | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([
		{ name: 'fk__rbac_role__id_manager', referencedColumnName: 'id' },
	])
	public managerRole!: RbacRoleEntity | null;

	/**
	 * The user's default / active warehouse (PD-13742). When set, the user
	 * operates "on behalf of" this warehouse: warehouse-coupled operations
	 * (dispatch, trip create, inventory moves, receiving) default to it and
	 * hide the picker, and warehouse-scoped lists default to it. Nullable — a
	 * user with no default still selects a warehouse per action. Must be one of
	 * the user's accessible warehouses (user_warehouse / company_warehouse);
	 * validated on write. Stored 1:1 on the user, alongside availabilityStatus.
	 */
	@Column({
		name: 'fk_warehouse__id_default',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultWarehouseId?: number | null;

	/**
	 * Last-known GPS position of the user (PD-13913). When GPS comes from the
	 * user's interface (a position-bearing action like a stop/inspection), it is
	 * stored here at the user level and also bubbled to the associated vehicle
	 * (see VehicleEntity). Lets list/detail views show where a user's event
	 * happened and powers maps that fall back to the user's position when the
	 * vehicle's is unknown. Never stored on the inventory location.
	 */
	@Column('decimal', {
		name: 'currentLatitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLatitude!: string | null;

	@Column('decimal', {
		name: 'currentLongitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLongitude!: string | null;

	@Column('datetime', { name: 'currentLocationAt', nullable: true })
	public currentLocationAt!: Date | null;

	@Column('varchar', {
		name: 'currentLocationSource',
		length: 32,
		nullable: true,
	})
	public currentLocationSource!: string | null;

	@ManyToOne(() => WarehouseEntity, { nullable: true })
	@JoinColumn({ name: 'fk_warehouse__id_default' })
	public defaultWarehouse?: WarehouseEntity;

	@OneToMany(
		() => VaultSecretsEntity,
		(vaultSecrets) => vaultSecrets.fkUser,
		{},
	)
	public vaultSecrets!: Array<VaultSecretsEntity>;

	@OneToMany(() => CallEntity, (call) => call.fkUser, {})
	public calls!: Array<CallEntity>;

	@OneToMany(() => CallUserEntity, (callUser) => callUser.fkUser, {})
	public callUsers!: Array<CallUserEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkUserIdAlternate2, {})
	public clientsAssignedAsAlternate!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkUser, {})
	public assignedClients!: Array<ClientEntity>;

	@OneToMany(
		() => ClientAssignmentHistoryEntity,
		(clientAssignmentHistory) => clientAssignmentHistory.fkUser,
		{},
	)
	public clientAssignmentHistories!: Array<ClientAssignmentHistoryEntity>;

	@OneToMany(() => EventUserEntity, (eventUser) => eventUser.fkUser, {})
	public eventUsers!: Array<EventUserEntity>;

	@OneToMany(() => IssueUserEntity, (issueUser) => issueUser.fkUser, {})
	public issueUsers!: Array<IssueUserEntity>;

	@OneToMany('IssueEntity', (issue: IssueEntity) => issue.assignee, {})
	public assignedIssues!: Array<IssueEntity>;

	@OneToMany(() => GoalEntity, (goal) => goal.fkUser, {})
	public goals!: Array<GoalEntity>;

	@OneToMany(
		() => MSmClientOpportunityEntity,
		(mSmClientOpportunity) => mSmClientOpportunity.mSmFkUser,
		{},
	)
	public mSmClientOpportunities!: Array<MSmClientOpportunityEntity>;

	@OneToMany(() => OrderEntity, (order) => order.submittedByUser, {})
	public submittedOrders!: Array<OrderEntity>;

	@OneToMany(() => OrderEntity, (order) => order.fkUserIdOverride2, {})
	public overriddenOrders!: Array<OrderEntity>;

	@OneToMany(() => OrderEntity, (order) => order.fkUser, {})
	public assignedOrders!: Array<OrderEntity>;

	@OneToMany(() => TeamUserEntity, (teamUser) => teamUser.fkUser, {})
	public teamUsers!: Array<TeamUserEntity>;

	@OneToOne(
		() => UserClientRestrictionsEntity,
		(userClientRestrictions) => userClientRestrictions.fkUser,
		{},
	)
	public userClientRestrictions!: UserClientRestrictionsEntity;

	@OneToMany(() => UserCompanyEntity, (userCompany) => userCompany.fkUser, {})
	public userCompanies!: Array<UserCompanyEntity>;

	@OneToMany(() => UserGroupEntity, (userGroup) => userGroup.fkUser, {})
	public userGroups!: Array<UserGroupEntity>;

	@OneToMany(
		() => UserWarehouseEntity,
		(userWarehouse) => userWarehouse.fkUser,
		{},
	)
	public userWarehouses!: Array<UserWarehouseEntity>;

	@OneToMany(
		() => ClientWorkdUserEntity,
		(clientWorkdUser) => clientWorkdUser.workdUser,
		{},
	)
	public clientWorkdUsers!: Array<ClientWorkdUserEntity>;

	@OneToMany(
		() => RbacActionUserEntity,
		(rbacActionUser) => rbacActionUser.user,
		{},
	)
	public rbacActionUsers!: Array<RbacActionUserEntity>;

	// Write-path safety net for the user's direct phone number — see
	// matching hook on `ClientEntity`. Note: `phoneExtensionNumber` is
	// a PBX extension digit string (not an E.164 phone), so it is NOT
	// normalized here.
	@BeforeInsert()
	@BeforeUpdate()
	// `protected` (not `private`): this hook is invoked by TypeORM via the
	// @BeforeInsert/@BeforeUpdate decorators, not by direct calls, so TS
	// `noUnusedLocals` (TS6133) would wrongly flag a `private` version as unused.
	protected _normalizePhone(): void {
		if (this.phoneNumber != null && this.phoneNumber !== '') {
			const normalized = normalizePhoneToE164(this.phoneNumber);
			if (normalized != null) {
				this.phoneNumber = normalized;
			}
		}
	}
}
